import { useState, useCallback, useEffect, memo } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Typography, makeStyles } from '@material-ui/core';
import PeoplePageContainer from '../../shared/peoplePageContainer';
import CustomButton from '../../shared/customButton';
import ManageActionPlan from '../../shared/manageActionPlan';
import ActionPlanCards from '../../shared/actionPlanCards';
import Filters from '../../shared/filters';
import NotificationCard from '../../shared/notificationCard';
import AlertDialog from '../../shared/alertDialog';
import ManageTasks from '../../shared/manageTasks';
import ConditionalTooltip from '../../shared/conditionalTooltip';
import { PLACEMENTS, GENERAL_CATEGORY_ID } from '../../../constants/tags';
import { PERMISSION_CODES } from '../../../constants/permissions';
import {
  api_action_plan,
  api_action_plan_tasks,
} from '../../../constants/apiRoutes';
import {
  APP_PAGES,
  PARAMS,
  PEOPLE_ACTION_PLANS_DEFAULT_PARAMS,
  PAGE_WHITELISTED_PARAMS,
} from '../../../constants/pages';
import { sticky } from '../../../constants/helperCssRules';
import { actionPlansSelector } from '../../../store/selectors/actionPlansSelector';
import { personSelector } from '../../../store/selectors/personSelector';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import { tagsSelector } from '../../../store/selectors/tagsSelector';
import { coursesSelector } from '../../../store/selectors/coursesSelector';
import {
  getUserActionPlans,
  clearAllActionPlans,
} from '../../../store/modules/actionPlans';
import {
  getTagsCategories,
  clearTagsCategories,
  addMultipleTags,
  getPageQuickFilters,
  clearPageQuickFilters,
} from '../../../store/modules/tags';
import {
  getCourseEnrolledUsers,
  clearCourseEnrolledUsers,
} from '../../../store/modules/courseDetails';
import { getCourses, clearCourses } from '../../../store/modules/courses';
import { getAllUsers, clearAllUsers } from '../../../store/modules/people';
import http from '../../../utility/http';
import {
  hasAuthPermissionCheck,
  isUserProfileAccessibleCheck,
  hasScopedAuthPermissionCheck,
} from '../../../utility/permissions';
import { useTranslations } from '../../../utility/useTranslations';
import {
  parseDuplicateParameters,
  hasNextPage,
  parseQueryParams,
  showSuccessMessage,
  hasSelectedFiltersCheck,
} from '../../../utility/uiUtils';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import {
  isArray,
  isObjectEmpty,
  isArrayEmpty,
  isUserDeactivated,
  trimString,
} from '../../../utility/helpers';
import {
  prepareTranslatedActionPlanStatuses,
  prepareActionPlanFilterStatuses,
  prepareActionPlansPageFilters,
} from '../../../utility/actionPlans';
import { prepareTasksForSave } from '../../../utility/tasks';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  stickyHeader: {
    ...sticky(primary.white, 170),
  },
  pageDescription: {
    marginBottom: spacing(4),
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: spacing(4),
    width: '100%',
  },
  statusFilter: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadMoreWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: spacing(4),
  },
  loadMore: {
    border: `1px solid ${primary.bluish3}`,
    borderRadius: 44,
    color: primary.bluish4,
    lineHeight: '24px',
    justifySelf: 'center',
    padding: '4px 16px',
    maxHeight: 32,
  },
  addButtonWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: spacing(2),
  },
  addButton: {
    width: 'max-content',
  },
  disabledUserAction: {
    cursor: 'not-allowed',
  },
}));

const { PAGE, DUE_DATE, TAGS, COMPLETION_STATUS } = PARAMS;

const PeopleActionPlansPage = ({
  auth,
  accessControl,
  organizationSettings,
  navigate,
  location,
  dispatch,
  ...rest
}) => {
  const { id } = useParams();
  const userId = (!Number.isNaN(Number(id)) && +id) || -1;

  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.PEOPLE_ACTION_PLANS);

  const user = useSelector(personSelector);
  const { actionPlans } = useSelector(actionPlansSelector);
  const { allUsers } = useSelector(peopleSelector);
  const { categories, pageQuickFilters } = useSelector(tagsSelector);
  const { courses } = useSelector(coursesSelector);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [page, setPage] = useState(PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE]);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [actionPlan, setActionPlan] = useState({});
  const [isManagePlanOpened, setIsManagePlanOpened] = useState(false);
  const [isDeleteOpened, setIsDeleteOpened] = useState(false);
  const [isDismissRequestOpened, setIsDismissRequestOpened] = useState(false);
  const [isManageTasksOpened, setIsManageTasksOpened] = useState(false);
  const [filters, setFilters] = useState({
    [COMPLETION_STATUS]: [],
    [TAGS]: [],
    [DUE_DATE]: [],
  });

  const canEdit = hasScopedAuthPermissionCheck(
    PERMISSION_CODES.PLANS_EDIT,
    accessControl,
    auth.id,
    userId
  );
  const canViewCourses = hasAuthPermissionCheck(
    PERMISSION_CODES.LEARNING_VIEW,
    accessControl
  );
  const statuses = prepareTranslatedActionPlanStatuses(translations.statuses);
  const statusFilters = prepareActionPlanFilterStatuses(translations.statuses);
  const showLoadMore = hasNextPage(actionPlans);
  const filterIsActive = hasSelectedFiltersCheck(filters);
  const isDeactivatedUser = isUserDeactivated(user);

  const fetchActionPlans = useCallback(
    (currentFilters, currentPage, isLoadMore = false) => {
      const params = {
        page: currentPage,
        ...currentFilters,
      };
      const query = parseDuplicateParameters(params);

      if (location.search !== `?${query}`) {
        navigate(`/people/${userId}/action-plans/?${query}`, {
          replace: true,
        });
      }

      return getUserActionPlans(dispatch, userId, params, isLoadMore);
    },
    [dispatch, location.search, navigate, userId]
  );

  const handlePageChange = useCallback(
    ({ isLoadMore = false, filtersOverride, pageOverride } = {}) => {
      const currentPage = pageOverride ?? page;
      const currentFilters = filtersOverride ?? filters;

      return fetchActionPlans(currentFilters, currentPage, isLoadMore);
    },
    [fetchActionPlans, filters, page]
  );

  const getInitialData = useCallback(async () => {
    const params = parseQueryParams(
      location.search,
      PAGE_WHITELISTED_PARAMS.PEOPLE_ACTION_PLANS
    );

    try {
      if (!isObjectEmpty(params)) {
        const urlFilters = {
          [COMPLETION_STATUS]: params[COMPLETION_STATUS]
            ? [params[COMPLETION_STATUS]]
            : [],
          [TAGS]: params[TAGS]
            ? [...(isArray(params[TAGS]) ? params[TAGS] : [params[TAGS]])]
            : [],
          [DUE_DATE]: params[DUE_DATE] ? [params[DUE_DATE]] : [],
        };

        setFilters(urlFilters);

        await Promise.all([
          fetchActionPlans(
            urlFilters,
            PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE]
          ),
          getTagsCategories(dispatch),
          getPageQuickFilters(dispatch, PLACEMENTS.ACTION_POINTS.id),
          getAllUsers(dispatch),
        ]);
      } else {
        await Promise.all([
          fetchActionPlans(
            {
              [COMPLETION_STATUS]: [],
              [TAGS]: [],
              [DUE_DATE]: [],
            },
            PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE]
          ),
          getTagsCategories(dispatch),
          getPageQuickFilters(dispatch, PLACEMENTS.ACTION_POINTS.id),
          getAllUsers(dispatch),
        ]);
      }
    } finally {
      setIsInitialLoad(false);
    }
  }, [dispatch, fetchActionPlans, location.search]);

  const cleanup = useCallback(() => {
    clearAllActionPlans(dispatch);
    dispatch(clearTagsCategories());
    dispatch(clearAllUsers());
    dispatch(clearPageQuickFilters());
    dispatch(clearCourses());
    dispatch(clearCourseEnrolledUsers());
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, userId]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const canAccessUserProfileCheck = currentUser =>
    isUserProfileAccessibleCheck(
      accessControl,
      auth.id,
      currentUser?.id || currentUser
    );

  const onLoadMore = () => {
    const newPage = page + 1;
    setPage(newPage);
    handlePageChange({
      isLoadMore: true,
      filtersOverride: filters,
      pageOverride: newPage,
    });
  };

  const handleApplyFilters = newFilters => {
    setPage(PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE]);
    setFilters(newFilters);
    setIsFilterLoading(true);

    handlePageChange({
      filtersOverride: newFilters,
      pageOverride: PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE],
    }).then(() => setIsFilterLoading(false));
  };

  const handleOpenManagePlan = () => setIsManagePlanOpened(true);

  const handleEditPlan = plan => {
    setIsManagePlanOpened(true);
    setActionPlan(plan);
  };

  const handleCloseManagePlan = () => {
    setIsManagePlanOpened(false);
    setIsDeleteOpened(false);
    setActionPlan({});
  };

  const onSaveActionPlan = () => {
    setPage(PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE]);
    handlePageChange({
      filtersOverride: filters,
      pageOverride: PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE],
    });
  };

  const handleOpenManageTasks = plan => {
    setIsManageTasksOpened(true);
    setActionPlan(plan);
  };

  const handleCloseManageTasks = () => {
    setIsManageTasksOpened(false);
    setActionPlan({});
  };

  const onSaveTasks = async values => {
    const isCreatedForCurrentUser = auth.id === actionPlan.user.id;
    const tasks = prepareTasksForSave(values.tasks);
    const data = {
      tasks,
      user: userId,
    };

    if (isCreatedForCurrentUser) {
      await http.post(api_action_plan_tasks(actionPlan.id), data);
    } else {
      await http.patch(api_action_plan(actionPlan.id), data);
    }
    showSuccessMessage(translations.manageTasksDialog.successMessage);

    setPage(PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE]);
    handlePageChange({
      filtersOverride: filters,
      pageOverride: PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE],
    });
  };

  const handleActionPlanDelete = plan => {
    setIsDeleteOpened(true);
    setActionPlan(plan);
  };

  const handleOpenActionPlanDelete = () => setIsDeleteOpened(true);

  const handleCloseActionPlanDelete = () => setIsDeleteOpened(false);

  const handleDismissProposal = plan => {
    setActionPlan(plan);
    setIsDismissRequestOpened(true);
  };

  const handleCloseDismissDialog = () => {
    setActionPlan({});
    setIsDismissRequestOpened(false);
  };

  const onActionPlanDelete = () => {
    const successMessage = isDismissRequestOpened
      ? translations.dismissActionPlanProposalDialog.deleteSuccess
      : translations.deleteActionPlanDialog.deleteSuccess;

    return http.delete(api_action_plan(actionPlan.id)).then(() => {
      showSuccessMessage(successMessage);
      setPage(PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE]);
      setIsDeleteOpened(false);
      setIsManagePlanOpened(false);
      setIsDismissRequestOpened(false);
      setActionPlan({});
      handlePageChange({
        filtersOverride: filters,
        pageOverride: PEOPLE_ACTION_PLANS_DEFAULT_PARAMS[PAGE],
      });
    }, handleCloseManagePlan);
  };

  const handleGoToPlanDetails = plan => {
    navigate(`/action-plans/${plan.id}/details`);
  };

  const onCreateTag = async tagName => {
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    dispatch(addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags }));

    return tag;
  };

  const handleCoursesSearch = value => {
    const text = trimString(value);
    const params = {
      ...(text ? { search: text } : {}),
    };

    return getCourses(dispatch, params);
  };

  const handleGetCourses = params => getCourses(dispatch, params);

  const handleGetCourseEnrolledUsers = (courseId, params) =>
    getCourseEnrolledUsers(dispatch, courseId, params);

  const goToUserProfilePage = profileUserId =>
    navigate(`/people/${profileUserId}`);

  return (
    <PeoplePageContainer
      {...rest}
      translations={translations}
      auth={auth}
      accessControl={accessControl}
      location={location}
      userId={userId}
      organizationSettings={organizationSettings}
      navigate={navigate}
      dispatch={dispatch}
      shouldPassProps={false}
    >
      {!isInitialLoad && (
        <div>
          <div className={classes.stickyHeader}>
            <Typography variant="body2" className={classes.pageDescription}>
              {auth.id === userId
                ? translations.descriptionOwnProfile
                : translations.pageDescription}
            </Typography>
            <div className={classes.filterContainer}>
              <Filters
                translations={translations.filters}
                selectedFilters={filters}
                filters={prepareActionPlansPageFilters(
                  translations,
                  statusFilters,
                  categories,
                  pageQuickFilters
                )}
                onApplyFilters={handleApplyFilters}
              />
              {canEdit && (
                <div className={classes.addButtonWrapper}>
                  <ConditionalTooltip
                    className={classes.disabledUserAction}
                    message={translations.disabledAddActionTooltip}
                    addTooltip={isDeactivatedUser}
                  >
                    <CustomButton
                      className={classes.addButton}
                      type="addRoundedNew"
                      aria-label="Add"
                      disabled={isDeactivatedUser}
                      onClick={handleOpenManagePlan}
                    >
                      {translations.addActionPlan}
                    </CustomButton>
                  </ConditionalTooltip>
                </div>
              )}
            </div>
          </div>
          <ActionPlanCards
            translations={translations.actionPlans}
            actionPlans={actionPlans?.results || []}
            statuses={statuses}
            auth={auth}
            accessControl={accessControl}
            user={user}
            isDisabled={isDeactivatedUser}
            canViewCourses={
              organizationSettings?.users_see_courses && canViewCourses
            }
            canAccessUserProfileCheck={canAccessUserProfileCheck}
            goToUserProfilePage={goToUserProfilePage}
            onCardClick={handleGoToPlanDetails}
            onEdit={handleEditPlan}
            onDelete={handleActionPlanDelete}
            onManageTasks={handleOpenManageTasks}
            onDismissProposal={handleDismissProposal}
          />
          <div className={classes.loadMoreWrapper}>
            {showLoadMore && (
              <CustomButton
                className={classes.loadMore}
                type="addWithTextRounded"
                onClick={onLoadMore}
              >
                {translations.loadMore}
              </CustomButton>
            )}
          </div>
          <NotificationCard
            title={filterIsActive ? '' : translations.noResultsTitle}
            content={
              filterIsActive
                ? translations.noSearchResultsMessage
                : translations.noResultsMessage
            }
            shouldFade={
              !isFilterLoading &&
              isArray(actionPlans.results) &&
              isArrayEmpty(actionPlans.results)
            }
            shouldFadeOutInstant
          />
          <ManageActionPlan
            translations={translations.manageActionPlan}
            isOpen={isManagePlanOpened}
            isInitialValid={!isObjectEmpty(actionPlan)}
            auth={auth}
            accessControl={accessControl}
            statuses={statuses}
            user={user}
            actionPlan={actionPlan}
            categories={categories}
            allEmployees={allUsers}
            courses={courses.results}
            getCourses={handleGetCourses}
            canSeeCourses={organizationSettings?.users_see_courses}
            getCourseEnrolledUsers={handleGetCourseEnrolledUsers}
            onCoursesSearch={handleCoursesSearch}
            onCreateTag={onCreateTag}
            onDelete={handleOpenActionPlanDelete}
            onClose={handleCloseManagePlan}
            onConfirm={onSaveActionPlan}
          />
          {actionPlan && (
            <ManageTasks
              translations={translations.manageTasksDialog}
              isOpened={isManageTasksOpened}
              record={actionPlan}
              availableUsers={allUsers}
              onClose={handleCloseManageTasks}
              onSave={onSaveTasks}
            />
          )}
          <AlertDialog
            isOpened={isDeleteOpened}
            translations={translations.deleteActionPlanDialog}
            onClose={handleCloseActionPlanDelete}
            onConfirm={onActionPlanDelete}
            isWarning
          />
          <AlertDialog
            translations={translations.dismissActionPlanProposalDialog}
            isOpened={isDismissRequestOpened}
            onClose={handleCloseDismissDialog}
            onConfirm={onActionPlanDelete}
            isWarning
          />
        </div>
      )}
    </PeoplePageContainer>
  );
};

PeopleActionPlansPage.propTypes = {
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  organizationSettings: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(PeopleActionPlansPage);
